import React, {
  FC,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useState,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { TIcons } from "../../type/icons-type";
import Item from "./Item";
import List from "./List";
import AddDashboard from "../../pages/presentation/common-component/AddDashboard";
import { fetchAllDashboardsData } from "../../store/apiSlice";
import { fetchDashboardInfoForUserId } from "../../store/dashboardForUserId";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useNavigate } from "react-router-dom";

interface INavigationLineProps {
  className?: string;
}
export const NavigationLine: FC<INavigationLineProps> = ({ className }) => {
  return <hr className={classNames("navigation-line", className)} />;
};
NavigationLine.propTypes = {
  className: PropTypes.string,
};
NavigationLine.defaultProps = {
  className: undefined,
};

interface INavigationTitleProps extends HTMLAttributes<HTMLSpanElement> {
  className?: string;
  children: ReactNode;
}
export const NavigationTitle: FC<INavigationTitleProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <li className="navigation-item">
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <span className={classNames("navigation-title", className)} {...props}>
        {children}
      </span>
    </li>
  );
};
NavigationTitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
NavigationTitle.defaultProps = {
  className: undefined,
};

interface INavigationProps {
  horizontal?: boolean;
  menu: {
    [key: string]: {
      id?: string | number;
      text?: string;
      path?: string;
      icon?: TIcons;
      isDisable?: boolean;
      subMenu?: {
        [key: string]: {
          id?: string | number;
          text?: string;
          path?: string;
          icon?: TIcons;
          isDisable?: boolean;
        };
      } | null;
    };
  };
  id: string;
  className?: string;
}

const Navigation = forwardRef<HTMLElement, INavigationProps>(
  ({ menu, horizontal, id, className, ...props }, ref) => {
    const [activeItem, setActiveItem] = useState(undefined);
    const [openPreviewDeleteSuccessDialog, setOpenPreviewDeleteSuccessDialog] =
      useState(false);
    const dispatch: AppDispatch = useDispatch();
    const vyu_api_url = process.env.REACT_APP_VYU_API_URL;
    const navigate = useNavigate();
    const { t } = useTranslation("menu");

    const handleItemClick = () => {
      setOpenPreviewDeleteSuccessDialog(true);
    };
    const handleDeleteSuccessConfirmation = () => {
      // deleteTab(data);
      setOpenPreviewDeleteSuccessDialog(false);
      navigate("/dashboard");
      let response = dispatch(
        fetchAllDashboardsData(
          `${vyu_api_url}/dashboard/getAllDashboardForCurrentUser`
        )
      );
      response.then((result: any) => {
        const dashboardData = result.payload.obj;
        const defaultDashboard = dashboardData.find(
          (dat: any) => dat.isDefault
        );

        if (defaultDashboard) {
          let response = dispatch(
            fetchDashboardInfoForUserId(
              `${vyu_api_url}/dashboard/getDashboardInfoForUserById?dashboardId=${defaultDashboard.id}`
            )
          );
        }
      });
    };
    function fillMenu(
      data:
        | {
            id?: string | number;
            text?: string;
            path?: string;
            icon?: TIcons;
            isDisable?: boolean;
            subMenu?:
              | {
                  id?: string | number;
                  text?: string;
                  path?: string;
                  icon?: TIcons;
                  isDisable?: boolean;
                }[]
              | undefined;
          }[]
        | any,
      parentId: string,
      rootId: string,
      isHorizontal: boolean | undefined,
      isMore: boolean | undefined
    ) {
      return (
        <>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {Object.keys(data).map((item) => {
              const itemData = data[item];
              return (
                <React.Fragment key={itemData.id}>
                  {itemData.path ? (
                    <Item
                      rootId={rootId}
                      id={itemData.id}
                      title={itemData.text}
                      icon={itemData.icon}
                      to={`${itemData.path}`}
                      parentId={parentId}
                      isHorizontal={isHorizontal}
                      setActiveItem={setActiveItem}
                      activeItem={activeItem}
                      notification={itemData.notification}
                      hide={itemData.hide}
                      onClickCallback={handleItemClick}
                    >
                      {!!itemData.subMenu &&
                        fillMenu(
                          itemData.subMenu,
                          itemData.id,
                          rootId,
                          isHorizontal,
                          undefined
                        )}
                    </Item>
                  ) : (
                    !isMore &&
                    !isHorizontal && (
                      <NavigationTitle key={itemData.id}>
                        {t(itemData.text) as ReactNode}
                      </NavigationTitle>
                    )
                  )}
                </React.Fragment>
              );
            })}

            {/* ✅ Place AddDashboard only once, outside the loop */}
            <AddDashboard
              open={openPreviewDeleteSuccessDialog}
              onclose={() => setOpenPreviewDeleteSuccessDialog(false)}
              confirmCallBack={handleDeleteSuccessConfirmation}
            />
          </div>
        </>
      );
    }

    return (
      // @ts-ignore
      // eslint-disable-next-line react/jsx-props-no-spreading
      <nav ref={ref} aria-label={id} className={className} {...props}>
        <List id={id} horizontal={horizontal}>
          {fillMenu(menu, id, id, horizontal, undefined)}
          {horizontal && (
            <Item
              rootId={`other-${id}`}
              title={t("More") as string}
              icon="MoreHoriz"
              isHorizontal
              isMore
            >
              {fillMenu(menu, `other-${id}`, `other-${id}`, false, true)}
            </Item>
          )}
        </List>
      </nav>
    );
  }
);
Navigation.displayName = "Navigation";
Navigation.propTypes = {
  horizontal: PropTypes.bool,
  // @ts-ignore
  menu: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string,
    path: PropTypes.string,
    icon: PropTypes.string,
    isDisable: PropTypes.bool,
    subMenu: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        text: PropTypes.string,
        path: PropTypes.string,
        icon: PropTypes.string,
        isDisable: PropTypes.bool,
      })
    ),
  }).isRequired,
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
};
Navigation.defaultProps = {
  horizontal: false,
  className: undefined,
};

export default Navigation;
